import { Divider, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { DeleteIcon } from '@/shared/assets/icons/delete-icon';
import { ResetFilterIcon } from '@/shared/assets/icons/reset-filter-icon';
import { SaveIcon } from '@/shared/assets/icons/save-icon';
import { Checkbox } from '@/shared/components/ui/checkbox/checkbox';
import { TTableViewMenuContentProps } from '@/shared/components/ui/data-table/components/table-view-menu/table-view-menu.types';
import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { useTableViewSettings } from '@/shared/features/filter-preferences/hooks/use-table-view-settings/use-table-view-settings';

export const TableViewMenuContent: React.FC<TTableViewMenuContentProps> = ({
  scopeKey,
  onClose,
}) => {
  const { t } = useTranslation();
  const { gridApi } = useDataTable();
  const {
    columns,
    hasStoredView,
    saveMine,
    removeMine,
    resetView,
    setColumnVisible,
  } = useTableViewSettings({ scopeKey, gridApi });

  const visibleCount = columns.filter(({ visible }) => visible).length;

  const run = (action: () => Promise<void> | void) => async () => {
    onClose();
    try {
      await action();
    } catch {
      // The save toast already surfaced the failure.
    }
  };

  return (
    <>
      {columns.length > 0 && (
        <>
          <Typography
            variant="h7"
            color="secondary"
            padding="0.25rem"
            gutterBottom
          >
            {t('ui:table_view.columns')}
          </Typography>
          {columns.map(({ colId, headerName, visible }) => {
            // Hiding the last one would leave an unreadable table.
            const isLastVisible = visible && visibleCount === 1;

            return (
              <MenuItem
                key={colId}
                icon={
                  <Checkbox
                    checked={visible}
                    disabled={isLastVisible}
                    readOnly
                    tabIndex={-1}
                  />
                }
                disabled={isLastVisible}
                tooltip={
                  isLastVisible ? t('ui:table_view.last_column') : undefined
                }
                onClick={() =>
                  isLastVisible ? undefined : setColumnVisible(colId, !visible)
                }
              >
                {headerName}
              </MenuItem>
            );
          })}
          <Divider />
        </>
      )}
      <MenuItem onClick={run(resetView)} icon={<ResetFilterIcon />}>
        {t('ui:table_view.reset')}
      </MenuItem>
      <Divider />
      <MenuItem onClick={run(saveMine)} icon={<SaveIcon />}>
        {t('ui:table_view.save_mine')}
      </MenuItem>
      {hasStoredView && (
        <MenuItem
          onClick={run(removeMine)}
          icon={<DeleteIcon />}
          variant="alert"
        >
          {t('ui:table_view.remove_mine')}
        </MenuItem>
      )}
    </>
  );
};
